import { useEffect, useState } from "react";
import { Sparkles, X, Loader2, MessageSquare, PhoneCall, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

/**
 * "Try this script" modal — runs a dry-run against the real AI generator and
 * shows what the lead will receive when this cadence step fires. Zero side
 * effects: no SMS sent, no call placed, nothing persisted.
 *
 * Props:
 *   step: SequenceStep being previewed (kind, angle, etc.)
 *   campaignId?: string — used to pull product_desc / value_prop / ICP
 *   onClose: () => void
 *
 * The preview button itself lives in CadenceEditor; this component is just the
 * modal that opens when the user clicks it.
 */
export default function CadenceStepPreview({ step, campaignId, onClose }) {
  const [leads, setLeads] = useState([]);
  const [selectedLeadId, setSelectedLeadId] = useState(""); // "" = sample
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  // Lazy-load up to 25 recent leads on open (small list keeps the picker snappy).
  useEffect(() => {
    if (!step) return;
    setLoadingLeads(true);
    (async () => {
      try {
        const { data } = await api.get("/leads", { params: { limit: 25 } });
        setLeads(Array.isArray(data) ? data : (data?.items || []));
      } catch (e) {
        // Non-fatal — user can still use the synthetic sample.
        setLeads([]);
      } finally {
        setLoadingLeads(false);
      }
    })();
    // Reset result whenever a different step is opened.
    setResult(null);
    setSelectedLeadId("");
  }, [step]);

  if (!step) return null;

  const canPreview = step.kind === "sms" || step.kind === "call";

  const runDryRun = async () => {
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.post("/campaigns/cadence-dry-run", {
        step,
        campaign_id: campaignId || null,
        lead_id: selectedLeadId || null,
      });
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Preview failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50 backdrop-blur-sm animate-in fade-in"
      data-testid="cadence-preview-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !running) onClose(); }}
    >
      <div className="bg-white border border-zinc-200 w-full max-w-xl shadow-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-zinc-200 flex items-start justify-between gap-3">
          <div>
            <div className="overline mb-1 flex items-center gap-2">
              <Sparkles className="w-3 h-3 text-[#002FA7]" /> dry-run preview
            </div>
            <h3 className="font-heading text-lg font-bold">
              {step.kind === "sms" ? "SMS" : step.kind === "call" ? "Call" : step.kind} · step {step.step}
              {step.angle && (
                <span className="ml-2 text-[10px] uppercase tracking-wider bg-zinc-900 text-white px-1.5 py-0.5 align-middle">
                  {step.angle}
                </span>
              )}
            </h3>
            <p className="text-xs text-zinc-500 mt-1 font-mono">
              day +{step.day_offset ?? 0} · {String(step.hour ?? 10).padStart(2, "0")}:{String(step.minute ?? 0).padStart(2, "0")} local
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={running}
            data-testid="cadence-preview-close"
            className="text-zinc-400 hover:text-zinc-900 p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto flex-1">
          {!canPreview ? (
            <div className="text-sm text-zinc-600 border border-zinc-200 bg-zinc-50 p-4">
              No preview for <span className="font-mono bg-white border border-zinc-200 px-1.5">{step.kind}</span> steps —
              only <strong>sms</strong> and <strong>call</strong> are generated by AI.
            </div>
          ) : (
            <>
              {/* Lead picker */}
              <label className="overline block mb-2">Preview against</label>
              <select
                value={selectedLeadId}
                onChange={(e) => setSelectedLeadId(e.target.value)}
                disabled={running || loadingLeads}
                data-testid="cadence-preview-lead-picker"
                className="w-full px-3 py-2 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 bg-white mb-3"
              >
                <option value="">— Sample lead (auto from campaign niche) —</option>
                {leads.map((l) => (
                  <option key={l.id} value={l.id}>
                    {`${l.name} · ${l.city || "?"}${l.state ? `, ${l.state}` : ""}`}
                  </option>
                ))}
              </select>
              <p className="text-xs text-zinc-500 mb-4">
                Pick a real lead to see exactly how this step will personalize against them.
                We use the same AI path the runner uses — zero side effects, nothing is sent.
              </p>

              {/* Run / Re-run button */}
              <button
                type="button"
                onClick={runDryRun}
                disabled={running}
                data-testid="cadence-preview-run"
                className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white px-4 py-2.5 text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                  (result ? <RefreshCw className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />)}
                {running ? "Generating…" : (result ? "Re-generate" : "Generate preview")}
              </button>

              {/* Result */}
              {result && (
                <div className="mt-5 space-y-3" data-testid="cadence-preview-result">
                  <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">
                    Output · personalized for <span className="text-zinc-900 font-bold">{result.lead_name}</span>
                    {result.sample_used && <span className="ml-1 text-amber-600">(synthetic sample)</span>}
                  </div>
                  {result.kind === "sms" && result.sms_template && (
                    <div className="flex items-start gap-2" data-testid="cadence-preview-sms">
                      <div className="w-7 h-7 bg-green-100 flex items-center justify-center shrink-0">
                        <MessageSquare className="w-3.5 h-3.5 text-green-700" />
                      </div>
                      <div className="bg-[#002FA7] text-white px-3.5 py-2.5 text-sm leading-relaxed max-w-[85%] whitespace-pre-wrap">
                        {result.sms_template}
                      </div>
                    </div>
                  )}
                  {result.kind === "call" && result.call_script && (
                    <div className="flex items-start gap-2" data-testid="cadence-preview-call">
                      <div className="w-7 h-7 bg-blue-100 flex items-center justify-center shrink-0">
                        <PhoneCall className="w-3.5 h-3.5 text-blue-700" />
                      </div>
                      <div className="border-l-2 border-[#002FA7] bg-zinc-50 px-3.5 py-2.5 text-sm leading-relaxed max-w-[95%] whitespace-pre-wrap">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1">Opener (Vapi will say this)</div>
                        {result.call_script}
                      </div>
                    </div>
                  )}
                  {result.ai_score != null && (
                    <div className="text-[10px] font-mono text-zinc-500 pt-2 border-t border-zinc-100">
                      AI score: <span className="font-bold text-zinc-900">{result.ai_score}/100</span>
                      {result.ai_label && <span className="ml-2">({result.ai_label})</span>}
                      <span className="ml-2">· agent: {result.agent_name}</span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-zinc-200 bg-zinc-50 flex items-center justify-between gap-2">
          <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">
            Dry-run — no SMS sent, no call placed
          </span>
          <button
            type="button"
            onClick={onClose}
            disabled={running}
            data-testid="cadence-preview-done"
            className="px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-zinc-700 hover:bg-zinc-100"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
